import { triangulateVectors } from '../helpers';
import { format as unitFormat } from '../../../../../../units';
import { resolveColor, colorOptions } from '../../../../../../colors';

// seriesInfo can be undefined when fewer signals are configured than the current inputMode needs
function formatPointValue(seriesInfo, rawValue) {
  if (!seriesInfo) return '';
  const { value, unit } = unitFormat(
    seriesInfo.unit,
    rawValue,
    seriesInfo.decimals,
    seriesInfo.customUnit
  );
  return `${value} ${unit}`;
}

function Point({
  fill,
  value,
  index,
  pointSeries,
  vizSize,
  scale,
  totalPoints,
  pointRadius,
  onTooltipChange,
  inputMode,
}) {
  const [X, Y] =
    inputMode === 'cartesian' ? [value.M1, value.M2] : triangulateVectors({ ...value });
  const isLast = index === totalPoints - 1;

  const tooltipData =
    inputMode === 'cartesian'
      ? {
          M1: formatPointValue(pointSeries.M1, value.M1),
          M2: formatPointValue(pointSeries.M2, value.M2),
          time: value.time,
          color: '',
          magnitude: value.magnitude,
        }
      : {
          M1: formatPointValue(pointSeries.M1, value.M1),
          M2: formatPointValue(pointSeries.M2, value.M2),
          M3: formatPointValue(pointSeries.M3, value.M3),
          time: value.time,
          color: '',
          magnitude: value.magnitude,
        };

  return (
    <circle
      className="point"
      onMouseOver={(event) => {
        onTooltipChange({
          pos: { x: event.clientX + 10, y: event.clientY + 10 },
          data: tooltipData,
        });
      }}
      onMouseOut={() => {
        onTooltipChange(null);
      }}
      cx={Number.parseFloat(vizSize) / 2 + Number.parseFloat(X) * scale}
      cy={Number.parseFloat(vizSize) / 2 + Number.parseFloat(Y) * scale}
      r={isLast ? pointRadius + 2 : pointRadius}
      fill={fill}
      stroke={isLast ? 'white' : 'transparent'}
      fillOpacity={(1 / totalPoints) * (index + 1)}
      strokeWidth={2}
    />
  );
}

export default function StarPlot({
  filteredData,
  configuration,
  series,
  vizSize,
  scale,
  onTooltipChange,
  inputMode,
}) {
  const starMode = configuration?.starMode;

  if (starMode === 'current' && filteredData.length === 0) return null;
  if (starMode !== 'current' && starMode !== 'over time') return null;

  const points = starMode === 'current' ? [filteredData.at(-1)] : filteredData;

  const pointSeries =
    inputMode === 'cartesian'
      ? {
          M1: series.find((s) => s.id === configuration.plot?.X) || series[0],
          M2: series.find((s) => s.id === configuration.plot?.Y) || series[1],
        }
      : {
          M1: series.find((s) => s.id === configuration.plot?.L1) || series[0],
          M2: series.find((s) => s.id === configuration.plot?.L2) || series[1],
          M3: series.find((s) => s.id === configuration.plot?.L3) || series[2],
        };

  const defaultColor = colorOptions[0].value;
  const fill = resolveColor(configuration.plot?.color) || defaultColor;

  const lastIndex = filteredData.length - 1;

  return (
    <g>
      {points.map((value, index_) => {
        const index = starMode === 'current' ? lastIndex : index_;
        const pointValue =
          inputMode === 'cartesian'
            ? {
                M1: value[configuration.plot?.X || series[0]?.id],
                M2: value[configuration.plot?.Y || series[1]?.id],
                time: value.time,
                magnitude: value.magnitude,
              }
            : {
                M1: value[configuration.plot?.L1 || series[0]?.id],
                M2: value[configuration.plot?.L2 || series[1]?.id],
                M3: value[configuration.plot?.L3 || series[2]?.id],
                time: value.time,
                magnitude: value.magnitude,
              };
        return (
          <Point
            key={index}
            value={pointValue}
            pointSeries={pointSeries}
            fill={fill}
            index={index}
            vizSize={vizSize}
            scale={scale}
            totalPoints={filteredData.length}
            pointRadius={configuration.pointRadius}
            onTooltipChange={onTooltipChange}
            inputMode={inputMode}
          />
        );
      })}
    </g>
  );
}
